import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles } from "lucide-react";

const DEFAULT_SYMBOLS = ["7", "💎", "👑", "🅱", "🔔", "🍒", "🍋"];
const DEFAULT_PAYS = { "7": 100, "💎": 50, "👑": 25, "🅱": 15, "🔔": 10, "🍒": 5, "🍋": 3 };
const DEFAULT_BG = "linear-gradient(180deg,#1a0a0a,#3a0808 50%,#1a0a0a)";

const TIER_COLORS = {
  mega: "from-red-600 to-yellow-500",
  major: "from-purple-600 to-pink-500",
  minor: "from-blue-600 to-cyan-400",
  mini: "from-emerald-600 to-teal-400",
};

export default function Slots() {
  const { slot_id } = useParams();
  const nav = useNavigate();
  const { currency, lang, updateBalance } = useApp();
  const [meta, setMeta] = useState(null); // {name, symbols, bg, ...}
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [reels, setReels] = useState(["7", "🅱", "🍒"]);
  const [spinningReels, setSpinningReels] = useState([false, false, false]);
  const [jackpots, setJackpots] = useState({});
  const [lastWin, setLastWin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jackpotFlash, setJackpotFlash] = useState(null);

  useEffect(() => {
    api.get("/games/slots/catalog").then(r => {
      const s = r.data.slots.find(x => x.id === slot_id);
      if (s) {
        setMeta(s);
        setReels(s.symbols.slice(0, 3));
      } else if (slot_id) {
        setMeta({ id: slot_id, name: "Slot", symbols: DEFAULT_SYMBOLS, bg: DEFAULT_BG, vol: "medium" });
      }
    }).catch(() => {});
    refreshJackpots();
    const iv = setInterval(refreshJackpots, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [slot_id]);

  const refreshJackpots = async () => {
    try {
      const r = await api.get("/games/slots/jackpots");
      setJackpots(r.data[currency] || {});
    } catch {}
  };

  const symbols = meta?.symbols || DEFAULT_SYMBOLS;
  const paytable = meta ? computePaytable(meta) : DEFAULT_PAYS;
  const bg = meta?.bg || DEFAULT_BG;

  const spin = async () => {
    if (!bet || bet <= 0) return toast.error("Invalid bet");
    setLoading(true);
    setLastWin(null);
    setJackpotFlash(null);
    setSpinningReels([true, true, true]);
    const anim = setInterval(() => {
      setReels(() => [
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
        symbols[Math.floor(Math.random() * symbols.length)],
      ]);
    }, 80);
    try {
      const r = await api.post("/games/slots/spin", { bet: Number(bet), currency, slot_id: slot_id || null });
      // stop reels sequentially
      setTimeout(() => {
        clearInterval(anim);
        // Stop reel 1
        setReels(prev => [r.data.reels[0], prev[1], prev[2]]);
        setSpinningReels([false, true, true]);
        setTimeout(() => {
          setReels(prev => [prev[0], r.data.reels[1], prev[2]]);
          setSpinningReels([false, false, true]);
          setTimeout(() => {
            setReels(r.data.reels);
            setSpinningReels([false, false, false]);
            updateBalance(currency, r.data.balance);
            setJackpots(r.data.jackpots?.[currency] || r.data.jackpots || {});
            if (r.data.jackpot_won > 0) {
              setJackpotFlash({ tier: r.data.jackpot_tier, amount: r.data.jackpot_won });
              setLastWin({ type: r.data.win_type, amount: r.data.payout });
              toast.success(`🎰 ${(r.data.jackpot_tier || "").toUpperCase()} JACKPOT! +${fmt(r.data.payout, currency)}`);
              // remove flash after 6 sec
              setTimeout(() => setJackpotFlash(null), 6000);
            } else if (r.data.payout > 0) {
              setLastWin({ type: r.data.win_type, amount: r.data.payout });
              toast.success(`+${fmt(r.data.payout, currency)} (${r.data.win_type})`);
            } else {
              toast.message("Try again!");
            }
            setLoading(false);
          }, 300);
        }, 250);
      }, 900);
    } catch (e) {
      clearInterval(anim);
      setSpinningReels([false, false, false]);
      toast.error(e?.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-8">
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="slots-back" onClick={() => nav("/slots")} className="btn-ghost px-3 py-1 rounded text-xs flex items-center gap-1"><ArrowLeft size={12}/>Back</button>
        <h1 className="font-serif text-3xl gold-text ml-2">{meta?.name || t(lang, "slots.title")}</h1>
        {meta?.vol && <span className="text-[10px] uppercase text-white/40 tracking-widest ml-2">{meta.vol} volatility</span>}
      </div>

      {/* 4-tier jackpot ticker */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {["mega","major","minor","mini"].map(tier=>(
          <div key={tier} data-testid={`jackpot-${tier}`}
               className={`relative overflow-hidden lux-card p-3 ${jackpotFlash?.tier === tier ? "ring-2 ring-[#d4af37] animate-pulse" : ""}`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${TIER_COLORS[tier]} opacity-30`}/>
            <div className="relative">
              <div className={`text-[9px] uppercase tracking-widest bg-gradient-to-r ${TIER_COLORS[tier]} bg-clip-text text-transparent font-bold`}>
                {tier.toUpperCase()}
              </div>
              <div className="font-serif text-lg gold-text truncate">{fmt(jackpots[tier] || 0, currency)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Machine */}
      <div className="rounded-2xl p-6 border-[6px] border-[#d4af37] relative overflow-hidden" style={{ background: bg }}>
        {jackpotFlash && (
          <div className="absolute inset-0 bg-black/85 z-30 flex flex-col items-center justify-center animate-pulse">
            <div className={`font-serif text-6xl mb-2 bg-gradient-to-r ${TIER_COLORS[jackpotFlash.tier]} bg-clip-text text-transparent`}>
              {jackpotFlash.tier.toUpperCase()}
            </div>
            <div className="font-serif text-5xl gold-text">JACKPOT!</div>
            <div className="font-serif text-3xl text-emerald-400 mt-2">+{fmt(jackpotFlash.amount, currency)}</div>
          </div>
        )}
        <div className="bg-black/60 rounded-xl p-4 grid grid-cols-3 gap-3 relative z-10">
          {reels.map((s, i) => (
            <div key={i} data-testid={`slot-reel-${i}`}
                 className={`bg-white rounded-lg h-32 flex items-center justify-center text-6xl shadow-inner ${spinningReels[i] ? "animate-bounce" : ""}`}>
              {s}
            </div>
          ))}
        </div>

        {lastWin && !jackpotFlash && (
          <div className="text-center mt-4 text-[#d4af37] font-serif text-xl" data-testid="slot-result">
            {lastWin.type} · +{fmt(lastWin.amount, currency)}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-4 relative z-10">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-widest text-white/60">{t(lang, "slots.bet")} ({currency})</label>
            <input data-testid="slot-bet-input" type="number" min="1" value={bet} onChange={e => setBet(e.target.value)}
                   className="w-full mt-1 px-4 py-3 rounded-md bg-black/50 border border-white/20 focus:border-[#d4af37] focus:outline-none"/>
            <div className="flex gap-1 mt-2">
              {[2, 5, 10].map(x => <button key={x} onClick={() => setBet(b => Number(b) * x)} className="btn-ghost px-2 py-0.5 rounded text-xs">×{x}</button>)}
              <button onClick={() => setBet(b => Math.max(1, Math.floor(Number(b) / 2)))} className="btn-ghost px-2 py-0.5 rounded text-xs">÷2</button>
            </div>
          </div>
          <button data-testid="slot-spin" onClick={spin} disabled={loading}
                  className="btn-gold px-10 py-5 rounded-md text-base">{loading ? "…" : t(lang, "slots.spin")}</button>
        </div>

        {/* Paytable */}
        <div className="mt-6 grid grid-cols-7 gap-2 text-xs relative z-10">
          {symbols.map((s, i) => (
            <div key={i} className="text-center bg-black/40 rounded p-2 border border-white/10">
              <div className="text-2xl">{s}</div>
              <div className="text-[10px] text-[#d4af37] mt-1">×{paytable[s] ?? "?"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function computePaytable(slot) {
  // Mirror backend logic for display
  const VOL = {
    low: { top: 60, diamond: 30, crown: 20, bar: 12, bell: 8, cherry: 4, lemon: 2 },
    medium: { top: 100, diamond: 50, crown: 25, bar: 15, bell: 10, cherry: 5, lemon: 3 },
    high: { top: 250, diamond: 100, crown: 40, bar: 20, bell: 12, cherry: 5, lemon: 2 },
    mega: { top: 500, diamond: 150, crown: 60, bar: 25, bell: 12, cherry: 4, lemon: 2 },
  };
  const keys = ["top", "diamond", "crown", "bar", "bell", "cherry", "lemon"];
  const scale = VOL[slot.vol] || VOL.medium;
  const out = {};
  slot.symbols.forEach((sy, i) => (out[sy] = scale[keys[i]]));
  return out;
}
